import { hoverDynamic } from './hover';

export function pushStateHandler(options = {}) {
  const { debug: enableDebug = false } = options;

  // デバッグ関数
  const debug = (...args) => {
    if (enableDebug || window.DRAWER_DEBUG) {
      console.log('[Drawer Debug]', ...args);
    }
  };

  debug('=== pushStateHandler initialized ===');

  const drawer = document.querySelector('.js-drawer-content');
  if (!drawer) {
    debug('Drawer element not found, aborting');
    return;
  }

  // 色値からRGB値を抽出する関数
  const extractRGB = (color) => {
    const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (rgbMatch) {
      return `${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}`;
    }
    if (color.startsWith('#')) {
      let hex = color.substring(1);
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      }
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `${r}, ${g}, ${b}`;
    }
    return color;
  };

  // ドロワーを閉じる
  function closeDrawer() {
    debug('closeDrawer called');
    document.body.classList.remove('is-drawer-active');
    setTimeout(() => {
      document.body.classList.remove('is-disable-scroll-direction');
    }, 100);
    const drawerStyle = document.querySelector('.drawer-color');
    drawer.innerHTML = '';
    if (drawerStyle) drawerStyle.remove();
  }

  // ドロワーを開く（UIのみ、コンテンツは既に構築済み）
  function openDrawer() {
    debug('openDrawer called');
    document.body.classList.remove('is-nav-active');
    document.body.classList.add('is-drawer-active', 'is-disable-scroll-direction');

    const hov = drawer.querySelectorAll('a, button, .js-hov');
    if (hov.length) {
      hov.forEach((el) => {
        hoverDynamic(el);
      });
    }
  }

  // ドロワーの色を設定
  function setDrawerColors(pc, bc) {
    const existingStyle = document.querySelector('.drawer-color');
    if (existingStyle) existingStyle.remove();

    const style = document.createElement('style');
    style.classList.add('drawer-color');
    document.head.appendChild(style);
    const drawerPc = pc || '#000000';
    const drawerBc = bc || '#ffffff';
    style.sheet.insertRule(`:root{--drawer-primarycolor: ${drawerPc};}`);
    style.sheet.insertRule(`:root{--drawer-primarycolor-rgb: ${extractRGB(drawerPc)};}`);
    style.sheet.insertRule(`:root{--drawer-bgcolor: ${drawerBc};}`);
    style.sheet.insertRule(`:root{--drawer-bgcolor-rgb: ${extractRGB(drawerBc)};}`);
  }

  // 現在のページのベースURL（ドロワーを開く前のURL）を記録
  let baseURL_path = window.location.pathname;
  if (!baseURL_path.endsWith('/')) baseURL_path += '/';
  debug('Base URL path:', baseURL_path);

  // bfcache（Back-Forward Cache）からの復元を検出
  // 戻るボタンでページが復元された場合、URLが変わっていたらリロード
  window.addEventListener('pageshow', (e) => {
    debug('pageshow event fired, persisted:', e.persisted);
    if (e.persisted) {
      // bfcacheから復元された
      let currentPath = window.location.pathname;
      if (!currentPath.endsWith('/')) currentPath += '/';
      debug('  Restored from bfcache, currentPath:', currentPath, 'baseURL_path:', baseURL_path);
      if (currentPath !== baseURL_path) {
        debug('  -> URL mismatch after bfcache restore, reloading');
        window.location.reload();
      }
    }
  });

  // popstateイベント: ブラウザの戻る/進むボタン
  window.addEventListener('popstate', (e) => {
    debug('popstate event fired');
    debug('  e.state:', e.state);
    debug('  current is-drawer-active:', document.body.classList.contains('is-drawer-active'));
    debug('  baseURL_path:', baseURL_path);
    debug('  current pathname:', window.location.pathname);

    // ドロワーが開いているなら閉じる
    if (document.body.classList.contains('is-drawer-active')) {
      debug('  -> closing drawer');
      closeDrawer();
      return;
    }

    // ドロワーが開いていない状態でpopstateが発火した場合、
    // 現在のパスがこのページのベースURLと異なるなら、実際にページ遷移する必要がある
    let currentPath = window.location.pathname;
    if (!currentPath.endsWith('/')) currentPath += '/';

    if (currentPath !== baseURL_path) {
      debug('  -> URL changed to different page, reloading:', currentPath);
      window.location.reload();
    }
  });

  // 共通のクリックハンドラを作成する関数
  function createDrawerTriggerHandler(type, dataAttr, dataSource, buildContent) {
    const triggers = document.querySelectorAll(`[data-${dataAttr}-id]`);
    debug(`${type} triggers found:`, triggers.length);

    if (!triggers.length) return;

    triggers.forEach((el) => {
      const href = el.href;
      const url = href.replace(baseURL, '');
      const id = el.dataset[`${dataAttr}Id`];

      el.addEventListener('click', function (e) {
        debug(`=== ${type.toUpperCase()} CLICK EVENT ===`);
        debug('  ID:', id);
        debug('  target URL:', url);
        e.preventDefault();

        let currentURL = window.location.pathname;
        if (!currentURL.endsWith('/')) currentURL += '/';
        debug('  current URL:', currentURL);

        // 現在のURLと同じの場合何もしない
        if (url === currentURL) {
          debug('  SKIPPED: same URL');
          return;
        }

        // データソースからアイテムを検索
        if (!id || typeof dataSource === 'undefined' || !dataSource) {
          debug(`  WARNING: ${dataAttr}id or ${dataAttr}Data missing`);
          return;
        }

        const foundElement = dataSource.find((element) => element['id'] == id);
        if (!foundElement) {
          debug('  WARNING: element not found in data');
          return;
        }

        debug('  Building drawer content for:', foundElement['title']);

        // ドロワーの中身をクリア
        drawer.innerHTML = '';
        const existingStyle = document.querySelector('.drawer-color');
        if (existingStyle) existingStyle.remove();

        // コンテンツを構築
        buildContent(foundElement, drawer);

        // 色を設定
        setDrawerColors(foundElement['primaryColor'], foundElement['bgColor']);

        // URLを更新（履歴に追加）
        history.pushState({ drawerOpen: true }, '', url);
        debug('  pushState completed:', url);

        // ドロワーを開く
        openDrawer();
      });
    });
  }

  // ===== 漫画ドロワー =====
  createDrawerTriggerHandler('manga', 'manga', typeof mangaData !== 'undefined' ? mangaData : null, (data, container) => {
    // MANGAラベル
    const labelContainer = document.createElement('div');
    labelContainer.classList.add('flex', 'is-justify-content-space-between', 'is-align-items-center', 'lh1_5');
    const mangaLabel = document.createElement('p');
    mangaLabel.classList.add('font-sans-serif-thin', 'ls005');
    mangaLabel.textContent = 'MANGA';
    labelContainer.appendChild(mangaLabel);
    container.appendChild(labelContainer);

    // タイトル
    if (data['title']) {
      const drawerTitle = document.createElement('h4');
      drawerTitle.classList.add('rowm-1', 'textsize-medium', 'font-sans-serif-bold', 'lh1_5');
      drawerTitle.innerHTML = data['title'];
      container.appendChild(drawerTitle);
    }

    // 著者
    if (data['authors']) {
      const length = data['authors'].length;
      const drawerAuthor = document.createElement('div');
      drawerAuthor.classList.add('rowm-0_5', 'textsize-medium', 'lh1_5');
      data['authors'].forEach((aa) => {
        const el = document.createElement('p');
        const role = aa['role'];
        const title = aa['title'];
        if (title) {
          if (role && length >= 2) {
            el.innerHTML = `${role}: ${title}`;
          } else {
            el.innerHTML = title;
          }
        }
        drawerAuthor.appendChild(el);
      });
      container.appendChild(drawerAuthor);
    }

    // 2カラムコンテナ
    const twoColumnContainer = document.createElement('div');
    twoColumnContainer.classList.add('flex', 'rowm-2_5');

    // 左カラム（画像）
    const leftColumn = document.createElement('div');
    leftColumn.classList.add('w-50', 'w-100-tablet');

    if (data['image']) {
      const imageContainer = document.createElement('a');
      const drawerImage = document.createElement('img');
      drawerImage.classList.add('img-max-svh');
      if (data['episodes'] && data['episodes'][0]) {
        const firstELink = data['episodes'][0]['permalink'];
        if (firstELink) imageContainer.setAttribute('href', firstELink);
      }
      drawerImage.setAttribute('src', data['image']);
      drawerImage.setAttribute('loading', 'lazy');
      if (data['imageW'] && data['imageH']) {
        drawerImage.style.aspectRatio = `${data['imageW']}/${data['imageH']}`;
      }
      if (data['episodes']) {
        imageContainer.appendChild(drawerImage);
        leftColumn.appendChild(imageContainer);
      } else {
        leftColumn.appendChild(drawerImage);
      }
    }

    // 右カラム
    const rightColumn = document.createElement('div');
    rightColumn.classList.add('w-50', 'w-100-tablet', 'pl-2', 'pt-2-tablet', 'pl-0-tablet');

    if (data['description']) {
      const drawerDescription = document.createElement('div');
      drawerDescription.classList.add('rowm-1_5', 'text', 'lh2');
      drawerDescription.innerHTML = data['description'];
      rightColumn.appendChild(drawerDescription);
    }

    twoColumnContainer.appendChild(leftColumn);
    twoColumnContainer.appendChild(rightColumn);

    // Buylinks
    if (data['buylinks'] && data['buylinks'].length) {
      data['buylinks'].forEach((buylinksGroup) => {
        const buylinksContainer = document.createElement('div');
        buylinksContainer.classList.add('rowm-4');
        const buylinksInner = document.createElement('div');
        buylinksInner.classList.add('posr');

        if (buylinksGroup['label']) {
          const headerEl = document.createElement('div');
          headerEl.classList.add('flex', 'is-justify-content-space-between', 'is-align-items-center');
          const labelEl = document.createElement('h3');
          labelEl.textContent = buylinksGroup['label'];
          headerEl.appendChild(labelEl);
          buylinksInner.appendChild(headerEl);
        }

        const hrContainer = document.createElement('div');
        hrContainer.classList.add('rowm-2', 'pl-1', 'pr-1');
        const hr = document.createElement('hr');
        hrContainer.appendChild(hr);
        buylinksInner.appendChild(hrContainer);

        const linksGrid = document.createElement('div');
        linksGrid.classList.add('rowm-2', 'flex-grid-1');
        linksGrid.setAttribute('data-row-gap', '1');

        buylinksGroup['links'].forEach((link) => {
          const linkWrapper = document.createElement('div');
          linkWrapper.classList.add('w-20', 'w-25-tablet', 'w-33-sp');

          const linkEl = document.createElement('a');
          linkEl.setAttribute('href', link['url']);
          linkEl.setAttribute('target', '_blank');
          linkEl.setAttribute('rel', 'noopener noreferrer');
          linkEl.classList.add('hover-scale');

          const linkImg = document.createElement('img');
          linkImg.setAttribute('src', link['image']);
          linkImg.setAttribute('alt', link['title']);
          linkImg.classList.add('hover-scale-target', 'bgcolor-white');

          linkEl.appendChild(linkImg);
          linkWrapper.appendChild(linkEl);
          linksGrid.appendChild(linkWrapper);
        });

        buylinksInner.appendChild(linksGrid);
        buylinksContainer.appendChild(buylinksInner);
        twoColumnContainer.appendChild(buylinksContainer);
      });
    }

    // エピソード一覧
    if (data['episodes']) {
      const drawerEpisodesContainer = document.createElement('div');
      drawerEpisodesContainer.classList.add('rowm-4', 'posr', 'w-100');

      const headerEl = document.createElement('div');
      headerEl.classList.add('flex', 'is-justify-content-space-between', 'is-align-items-center');
      const labelEl = document.createElement('h3');
      labelEl.textContent = 'エピソード一覧';
      headerEl.appendChild(labelEl);
      drawerEpisodesContainer.appendChild(headerEl);

      const drawerEpisodesGrid = document.createElement('div');
      drawerEpisodesGrid.classList.add('rowm-2', 'line-container', 'line-container-3', 'line-container-2-sp');

      data['episodes'].forEach((ee) => {
        const el = document.createElement('a');
        el.classList.add('db', 'posr', 'line-box', 'hover-opacity', 'hover-scale', 'pt-2', 'pb-2', 'pl-2', 'pr-2');
        el.setAttribute('href', ee['permalink']);

        const title = ee['title'];
        const titleSub = ee['titleSub'];
        const images = ee['images'];

        if (images && images.length > 0) {
          const img = document.createElement('img');
          img.classList.add('hover-scale-target', 'db', 'aspect-3-4', 'object-fit-contain');
          img.setAttribute('src', images[0]);
          img.setAttribute('alt', title);
          img.setAttribute('loading', 'lazy');
          el.appendChild(img);
        } else {
          const svgNS = 'http://www.w3.org/2000/svg';
          const svg = document.createElementNS(svgNS, 'svg');
          svg.classList.add('aspect-3-4', 'db', 'bgcolor-primarysub', 'fill-bg', 'hover-scale-target');
          svg.setAttribute('xmlns', svgNS);
          svg.setAttribute('viewBox', '0 0 2000 2000');
          const path = document.createElementNS(svgNS, 'path');
          path.setAttribute('d', 'M513.591 882.789c-44.935 0-80.094-33.203-80.094-78.139 0-44.927 35.159-78.138 80.094-78.138h191.435c46.883 0 80.094 33.21 80.094 78.138 0 44.936-33.211 78.139-80.094 78.139H513.591zM513.591 1078.142c-44.935 0-80.094-33.21-80.094-78.138 0-44.936 35.159-78.138 80.094-78.138h972.818c46.883 0 80.094 33.202 80.094 78.138 0 44.928-33.211 78.138-80.094 78.138H513.59zM611.26 687.45c-44.928 0-80.094-33.21-80.094-78.137 0-44.936 35.166-78.139 80.094-78.139h777.48c46.883 0 80.086 33.203 80.086 78.139 0 44.927-33.203 78.138-80.086 78.138H611.26zM611.26 1468.842c-44.928 0-80.094-33.227-80.094-78.147 0-44.935 35.166-78.138 80.094-78.138h777.48c46.883 0 80.086 33.203 80.086 78.138 0 44.92-33.203 78.147-80.086 78.147H611.26zM708.937 492.097c-44.928 0-80.094-33.203-80.094-78.138 0-44.928 35.166-78.138 80.094-78.138h582.126c46.883 0 80.094 33.21 80.094 78.138 0 44.935-33.21 78.138-80.094 78.138H708.937zM708.937 1664.18c-44.928 0-80.094-33.203-80.094-78.139 0-44.92 35.166-78.138 80.094-78.138h582.126c46.883 0 80.094 33.219 80.094 78.138 0 44.936-33.21 78.138-80.094 78.138H708.937zM1294.974 1273.48c-44.935 0-80.094-33.203-80.094-78.138 0-44.92 35.159-78.139 80.094-78.139h191.435c46.883 0 80.094 33.22 80.094 78.139 0 44.935-33.211 78.138-80.094 78.138h-191.435z');
          svg.appendChild(path);
          el.appendChild(svg);
        }

        const titleEl = document.createElement('p');
        titleEl.classList.add('rowm-1', 'lh1_5', 'hover-opacity-target');
        const titleB = document.createElement('b');
        titleB.innerHTML = title;
        titleEl.appendChild(titleB);
        el.appendChild(titleEl);

        if (titleSub) {
          const titleSubEl = document.createElement('p');
          titleSubEl.classList.add('rowm-0_5', 'textsize-small', 'lh1_5', 'hover-opacity-target');
          titleSubEl.innerHTML = titleSub;
          el.appendChild(titleSubEl);
        }

        drawerEpisodesGrid.appendChild(el);
      });

      drawerEpisodesContainer.appendChild(drawerEpisodesGrid);
      twoColumnContainer.appendChild(drawerEpisodesContainer);
    }

    // 詳細リンク
    if (data['permalink']) {
      const drawerWorkLinkContainer = document.createElement('p');
      drawerWorkLinkContainer.classList.add('rowm-4', 'w-100', 'flex', 'is-justify-content-center');
      const drawerWorkLink = document.createElement('a');
      drawerWorkLink.classList.add('button', 'is-negative');
      drawerWorkLink.setAttribute('href', data['permalink']);
      drawerWorkLink.innerHTML = 'この漫画について更に詳しく見る';
      drawerWorkLinkContainer.appendChild(drawerWorkLink);
      twoColumnContainer.appendChild(drawerWorkLinkContainer);
    }

    container.appendChild(twoColumnContainer);
  });

  // ===== 出版物ドロワー =====
  createDrawerTriggerHandler('publication', 'publication', typeof publicationData !== 'undefined' ? publicationData : null, (data, container) => {
    // PUBLICATIONラベル
    const labelContainer = document.createElement('div');
    labelContainer.classList.add('flex', 'is-justify-content-space-between', 'is-align-items-center', 'lh1_5');
    const publicationLabel = document.createElement('p');
    publicationLabel.classList.add('font-sans-serif-thin', 'ls005');
    publicationLabel.textContent = 'PUBLICATION';
    labelContainer.appendChild(publicationLabel);
    container.appendChild(labelContainer);

    // タイトル
    if (data['title']) {
      const drawerTitle = document.createElement('h4');
      drawerTitle.classList.add('rowm-1', 'textsize-medium', 'font-sans-serif-bold', 'lh1_5');
      drawerTitle.innerHTML = data['title'];
      container.appendChild(drawerTitle);
    }

    // 著者
    if (data['authors']) {
      const length = data['authors'].length;
      const drawerAuthor = document.createElement('div');
      drawerAuthor.classList.add('rowm-0_5', 'textsize-medium', 'lh1_5');
      data['authors'].forEach((aa) => {
        const el = document.createElement('p');
        const role = aa['role'];
        const title = aa['title'];
        if (title) {
          if (role && length >= 2) {
            el.innerHTML = `${role}: ${title}`;
          } else {
            el.innerHTML = title;
          }
        }
        drawerAuthor.appendChild(el);
      });
      container.appendChild(drawerAuthor);
    }

    // 2カラムコンテナ
    const twoColumnContainer = document.createElement('div');
    twoColumnContainer.classList.add('flex', 'rowm-2_5');

    // 左カラム（画像）
    const leftColumn = document.createElement('div');
    leftColumn.classList.add('w-50', 'w-100-tablet');

    if (data['image']) {
      const drawerImage = document.createElement('img');
      drawerImage.classList.add('img-max-svh');
      drawerImage.setAttribute('src', data['image']);
      drawerImage.setAttribute('loading', 'lazy');
      if (data['imageW'] && data['imageH']) {
        drawerImage.style.aspectRatio = `${data['imageW']}/${data['imageH']}`;
      }
      leftColumn.appendChild(drawerImage);
    }

    // 右カラム
    const rightColumn = document.createElement('div');
    rightColumn.classList.add('w-50', 'w-100-tablet', 'pl-2', 'pt-2-tablet', 'pl-0-tablet');

    if (data['spec']) {
      const drawerSpec = document.createElement('div');
      drawerSpec.classList.add('rowm-1_5', 'text', 'textsize-small', 'lh2');
      drawerSpec.innerHTML = data['spec'];
      rightColumn.appendChild(drawerSpec);
    }

    if (data['description']) {
      const drawerDescription = document.createElement('div');
      drawerDescription.classList.add('rowm-1_5', 'text', 'lh2');
      drawerDescription.innerHTML = data['description'];
      rightColumn.appendChild(drawerDescription);
    }

    twoColumnContainer.appendChild(leftColumn);
    twoColumnContainer.appendChild(rightColumn);

    // Buylinks
    if (data['buylinks'] && data['buylinks'].length) {
      data['buylinks'].forEach((buylinksGroup) => {
        const buylinksContainer = document.createElement('div');
        buylinksContainer.classList.add('rowm-4', 'pr-2', 'pl-2', 'pl-1-sp', 'pr-1-sp');

        const buylinksInner = document.createElement('div');
        buylinksInner.classList.add('bt', 'posr');

        if (buylinksGroup['label']) {
          const labelEl = document.createElement('p');
          labelEl.classList.add('content-label');
          const labelB = document.createElement('b');
          labelB.textContent = buylinksGroup['label'];
          labelEl.appendChild(labelB);
          buylinksInner.appendChild(labelEl);
        }

        const linksGrid = document.createElement('div');
        linksGrid.classList.add('rowm-2', 'flex-grid-1');
        linksGrid.setAttribute('data-row-gap', '1');

        buylinksGroup['links'].forEach((link) => {
          const linkWrapper = document.createElement('div');
          linkWrapper.classList.add('w-20', 'w-25-pc-small', 'w-33-sp');

          const linkEl = document.createElement('a');
          linkEl.setAttribute('href', link['url']);
          linkEl.setAttribute('target', '_blank');
          linkEl.setAttribute('rel', 'noopener noreferrer');
          linkEl.classList.add('hover-scale');

          const linkImg = document.createElement('img');
          linkImg.setAttribute('src', link['image']);
          linkImg.setAttribute('alt', link['title']);
          linkImg.classList.add('hover-scale-target', 'bgcolor-white');

          linkEl.appendChild(linkImg);
          linkWrapper.appendChild(linkEl);
          linksGrid.appendChild(linkWrapper);
        });

        buylinksInner.appendChild(linksGrid);
        buylinksContainer.appendChild(buylinksInner);
        twoColumnContainer.appendChild(buylinksContainer);
      });
    }

    // 詳細リンク
    if (data['permalink']) {
      const drawerWorkLinkContainer = document.createElement('p');
      drawerWorkLinkContainer.classList.add('rowm-4', 'w-100', 'flex', 'is-justify-content-center');
      const drawerWorkLink = document.createElement('a');
      drawerWorkLink.classList.add('button', 'is-negative');
      drawerWorkLink.setAttribute('href', data['permalink']);
      drawerWorkLink.innerHTML = 'この出版物について更に詳しく見る';
      drawerWorkLinkContainer.appendChild(drawerWorkLink);
      twoColumnContainer.appendChild(drawerWorkLinkContainer);
    }
    
    container.appendChild(twoColumnContainer);
  });

  // ===== アートワークドロワー =====
  createDrawerTriggerHandler('artwork', 'artwork', typeof artworkData !== 'undefined' ? artworkData : null, (data, container) => {
    // ARTWORKラベル
    const labelContainer = document.createElement('div');
    labelContainer.classList.add('flex', 'is-justify-content-space-between', 'is-align-items-center', 'lh1_5');
    const artworkLabel = document.createElement('p');
    artworkLabel.classList.add('font-sans-serif-thin', 'ls005');
    artworkLabel.textContent = 'ARTWORK';
    labelContainer.appendChild(artworkLabel);
    container.appendChild(labelContainer);

    // タイトル
    if (data['title']) {
      const drawerTitle = document.createElement('h4');
      drawerTitle.classList.add('rowm-1', 'textsize-medium', 'font-sans-serif-bold', 'lh1_5');
      drawerTitle.innerHTML = data['title'];
      container.appendChild(drawerTitle);
    }

    // 著者
    if (data['authors']) {
      const length = data['authors'].length;
      const drawerAuthor = document.createElement('div');
      drawerAuthor.classList.add('rowm-0_5', 'textsize-medium', 'lh1_5');
      data['authors'].forEach((aa) => {
        const el = document.createElement('p');
        const role = aa['role'];
        const title = aa['title'];
        if (title) {
          if (role && length >= 2) {
            el.innerHTML = `${role}: ${title}`;
          } else {
            el.innerHTML = title;
          }
        }
        drawerAuthor.appendChild(el);
      });
      container.appendChild(drawerAuthor);
    }

    // 2カラムコンテナ
    const twoColumnContainer = document.createElement('div');
    twoColumnContainer.classList.add('flex', 'rowm-2_5');

    // 左カラム（画像）
    const leftColumn = document.createElement('div');
    leftColumn.classList.add('w-50', 'w-100-tablet');

    if (data['image']) {
      const drawerImage = document.createElement('img');
      drawerImage.classList.add('img-max-svh');
      drawerImage.setAttribute('src', data['image']);
      drawerImage.setAttribute('loading', 'lazy');
      if (data['imageW'] && data['imageH']) {
        drawerImage.style.aspectRatio = `${data['imageW']}/${data['imageH']}`;
      }
      leftColumn.appendChild(drawerImage);
    }

    // 右カラム
    const rightColumn = document.createElement('div');
    rightColumn.classList.add('w-50', 'w-100-tablet', 'pl-2', 'pt-2-tablet', 'pl-0-tablet');

    if (data['description']) {
      const drawerDescription = document.createElement('div');
      drawerDescription.classList.add('rowm-1_5', 'text', 'lh2');
      drawerDescription.innerHTML = data['description'];
      rightColumn.appendChild(drawerDescription);
    }

    twoColumnContainer.appendChild(leftColumn);
    twoColumnContainer.appendChild(rightColumn);

    // 詳細リンク
    if (data['permalink']) {
      const drawerWorkLinkContainer = document.createElement('p');
      drawerWorkLinkContainer.classList.add('rowm-4', 'w-100', 'flex', 'is-justify-content-center');
      const drawerWorkLink = document.createElement('a');
      drawerWorkLink.classList.add('button', 'is-negative');
      drawerWorkLink.setAttribute('href', data['permalink']);
      drawerWorkLink.innerHTML = 'このアートワークについて更に詳しく見る';
      drawerWorkLinkContainer.appendChild(drawerWorkLink);
      twoColumnContainer.appendChild(drawerWorkLinkContainer);
    }

    container.appendChild(twoColumnContainer);
  });

  // ===== 人物ドロワー =====
  createDrawerTriggerHandler('author', 'author', typeof authorData !== 'undefined' ? authorData : null, (data, container) => {
    // AUTHORラベル
    const labelContainer = document.createElement('div');
    labelContainer.classList.add('flex', 'is-justify-content-space-between', 'is-align-items-center', 'lh1_5');
    const authorLabel = document.createElement('p');
    authorLabel.classList.add('font-sans-serif-thin', 'ls005');
    authorLabel.textContent = 'AUTHOR';
    labelContainer.appendChild(authorLabel);
    container.appendChild(labelContainer);

    const hr = document.createElement('hr');
    hr.classList.add('rowm-1');
    container.appendChild(hr);

    // タイトル
    if (data['title']) {
      const drawerTitle = document.createElement('h4');
      drawerTitle.classList.add('rowm-2', 'textsize-medium', 'font-sans-serif-bold', 'lh1_5');
      drawerTitle.innerHTML = data['title'];
      container.appendChild(drawerTitle);
    }

    // 2カラムコンテナ
    const twoColumnContainer = document.createElement('div');
    twoColumnContainer.classList.add('flex', 'rowm-2_5');

    // 左カラム（画像）
    const leftColumn = document.createElement('div');
    leftColumn.classList.add('w-50', 'w-100-tablet');

    if (data['image']) {
      const drawerImage = document.createElement('img');
      drawerImage.classList.add('img-max-svh');
      drawerImage.setAttribute('src', data['image']);
      drawerImage.setAttribute('loading', 'lazy');
      if (data['imageW'] && data['imageH']) {
        drawerImage.style.aspectRatio = `${data['imageW']}/${data['imageH']}`;
      }
      leftColumn.appendChild(drawerImage);
    }

    // 右カラム
    const rightColumn = document.createElement('div');
    rightColumn.classList.add('w-50', 'w-100-tablet', 'pl-2', 'pt-2-tablet', 'pl-0-tablet');

    if (data['description']) {
      const drawerDescription = document.createElement('div');
      drawerDescription.classList.add('rowm-1_5', 'text', 'lh2');
      drawerDescription.innerHTML = data['description'];
      rightColumn.appendChild(drawerDescription);
    }

    twoColumnContainer.appendChild(leftColumn);
    twoColumnContainer.appendChild(rightColumn);

    // 詳細リンク
    if (data['permalink']) {
      const drawerWorkLinkContainer = document.createElement('p');
      drawerWorkLinkContainer.classList.add('rowm-4', 'w-100', 'flex', 'is-justify-content-center');
      const drawerWorkLink = document.createElement('a');
      drawerWorkLink.classList.add('button', 'is-negative');
      drawerWorkLink.setAttribute('href', data['permalink']);
      drawerWorkLink.innerHTML = 'この人物について更に詳しく見る';
      drawerWorkLinkContainer.appendChild(drawerWorkLink);
      twoColumnContainer.appendChild(drawerWorkLinkContainer);
    }

    container.appendChild(twoColumnContainer);
  });

  // ===== ドロワー閉じるトリガー（trigger-close）=====
  // 注意: trigger.jsより先にこのリスナーが実行される必要がある
  // capture: trueでキャプチャフェーズで実行し、stopImmediatePropagationで他のリスナーを止める
  const triggerCloseButtons = document.querySelectorAll('.trigger-close');
  debug('Trigger close buttons found:', triggerCloseButtons.length);
  if (triggerCloseButtons.length) {
    triggerCloseButtons.forEach((el) => {
      el.addEventListener('click', function (e) {
        debug('trigger-close clicked');
        debug('  current is-drawer-active:', document.body.classList.contains('is-drawer-active'));
        // ドロワーが開いている場合、history.backで戻る（popstateでドロワーが閉じる）
        if (document.body.classList.contains('is-drawer-active')) {
          debug('  -> calling history.back()');
          e.preventDefault();
          e.stopImmediatePropagation(); // trigger.jsのリスナーを止める
          history.back();
        }
        // そうでない場合はtrigger.jsの処理に任せる（クラス削除のみ）
      }, true); // captureフェーズで実行
    });
  }
}
